import { createReducer } from "reduxsauce"
import createActions from "helpers/actionCreator"
import { call, getContext, put, select, takeLatest } from "redux-saga/effects"
import { chain, get, pick } from "lodash"
import update from "immutability-helper"
import { DESC } from "helpers/sorts"
import { Types as LoaderTypes } from "model/data"
import { Types as GQLLoaderTypes } from "model/graphql"
import { Types as IntradayTypes } from "modules/intraday/model"
import { Types as PortfolioRootTypes } from "modules/portfolio-root/model"
import { assetClickHandler, getTracking } from "helpers/assets"
import { getPortfolio } from "modules/portfolio-root/selectors/portfolios"
import { getSegment } from "modules/asset-analytics/selectors"
import { getSelectedGroup } from "./selectors"

export const { Types, Creators } = createActions("ompnt/modules/intraday-asset-table", {
  hideGroup: [],
  itemClick: {
    props: ["screen", "category", "item", "column"],
    ...getTracking("intraday-assets"),
  },
  setMetricType: {
    props: ["metricType"],
    track: true,
    trackType: "intraday-assets-metric-type-selected",
    trackPayload: ({ metricType }) => ({ metricType }),
  },
  setColumnOrder: {
    props: ["columnOrder"],
    track: true,
    trackType: "intraday-assets-column-order-selected",
    trackPayload: ({ columnOrder }) => ({ columnOrder }),
  },
  setPage: {
    props: ["page"],
    track: true,
    trackType: "intraday-assets-page-updated",
    trackPayload: ({ page }) => ({ page }),
  },
  setSelectedGroup: {
    props: ["id"],
    track: true,
    trackType: "intraday-assets-grouping-selected",
    trackPayload: ({ id }) => ({ id }),
  },
  setSelectedView: {
    props: ["id"],
    track: true,
    trackType: "intraday-assets-view-selected",
    trackPayload: ({ id }) => ({ id }),
  },
  setSelectedSubView: {
    props: ["subView"],
    track: true,
    trackType: "intraday-assets-sub-view-selected",
    trackPayload: ({ subView }) => ({ subView }),
  },
  setSort: {
    props: ["sort"],
    track: true,
    trackType: "intraday-assets-sort-updated",
    trackPayload: ({ sort }) => pick(sort, ["type", "id", "direction"]),
  },
})

const DEFAULT_STATE = {
  columnOrder: "default",
  page: 0,
  sort: {
    id: "percentEquity",
    direction: DESC,
  },
  selectedGroup: null,
  selectedMetricType: null,
  selectedSubView: null,
}

const handleSetPage = (state, { page }) => ({ ...state, page })

const handleSetSort = (state, { sort }) => ({ ...state, sort })

const handleHideGroup = state => ({ ...state, selectedGroup: null, page: 0 })

const handleSetSelectedGroup = (state, { id }) => ({ ...state, selectedGroup: id, page: 0 })

const handleLoadSuccess = (state, { resource }) => resource === "portfolio" ? update(state, { $set: DEFAULT_STATE }) : state

const handleSetExpansionMode = state => ({ ...state, page: 0 })

const handleSetMetricType = (state, { metricType }) => ({ ...state, selectedMetricType: metricType })

function handleSetColumnOrder(state, { columnOrder }) {
  return { ...state, columnOrder: columnOrder }
}

const handleSetSelectedView = state => ({ ...state, selectedMetricType: null, selectedGroup: null, page: 0 })

const handleSetSelectedSubView = (state, { subView }) => ({ ...state, selectedSubView: subView })

export default createReducer(DEFAULT_STATE, {
  [Types.SET_PAGE]: handleSetPage,
  [Types.SET_SORT]: handleSetSort,
  [Types.HIDE_GROUP]: handleHideGroup,
  [Types.SET_SELECTED_GROUP]: handleSetSelectedGroup,
  [Types.SET_METRIC_TYPE]: handleSetMetricType,
  [Types.SET_COLUMN_ORDER]: handleSetColumnOrder,
  [Types.SET_SELECTED_VIEW]: handleSetSelectedView,
  [Types.SET_SELECTED_SUB_VIEW]: handleSetSelectedSubView,
  [IntradayTypes.SET_GROUPING]: handleHideGroup,
  [LoaderTypes.LOAD_SUCCESS]: handleLoadSuccess,
  [PortfolioRootTypes.SET_EXPANSION_MODE]: handleSetExpansionMode,
})

const selectedGroupExists = (selectedGroup, data) => chain(data)
  .get("model.intradaySimulation.composition.0.compositionBy", [])
  .some(group => group.id === selectedGroup)
  .value()

function* handleItemClick(params) {
  const stateParams = yield select(state => ({
    account: get(state, "global.selected.account"),
    portfolio: getPortfolio(state),
    currentGrouping: get(state, "modules.intraday.grouping"),
    segment: getSegment(state) || {},
    // Cannot reuse the selector defined in ./selectors because it would create a circular dependency
    groups: get(state, "global.data.intradayGroupedComposition.data.model.simulation.composition.0.compositionBy"),
  }))

  yield call(assetClickHandler, { ...params, ...stateParams })
}

function* handleGQLLoadSuccess({ resource, data }) {
  if (resource === "intradayGroupedComposition") {
    const selectedGroup = yield select(getSelectedGroup)

    if (selectedGroup) {
      if (!selectedGroupExists(selectedGroup, data)) {
        yield put(Creators.hideGroup())
      }
    }
  }
}

function* handleSetSelectedViewSaga({ id }) {
  const { account, portfolio } = yield select(state => ({
    account: get(state, "global.selected.account"),
    portfolio: getPortfolio(state),
  }))

  let url = ""
  if (id !== "summary") {
    url = `/${id}`
  }

  const { navigate } = yield getContext("router")
  yield call(navigate, `/${account}/p/${portfolio.shortId}/analyze/intraday/assets${url}`)
}

function* handleIntradayLoadSuccess({ data }) {
  const intradayGroupedComposition = get(data, "intradayGroupedComposition.data")
  if (intradayGroupedComposition) {
    const selectedGroup = yield select(getSelectedGroup)

    if (selectedGroup) {
      if (!selectedGroupExists(selectedGroup, intradayGroupedComposition)) {
        yield put(Creators.hideGroup())
      }
    }
  }
}

export function* saga() {
  yield takeLatest(Types.ITEM_CLICK, handleItemClick)
  yield takeLatest(Types.SET_SELECTED_VIEW, handleSetSelectedViewSaga)
  yield takeLatest(GQLLoaderTypes.LOAD_SUCCESS, handleGQLLoadSuccess)
  yield takeLatest(LoaderTypes.INTRADAY_LOAD_SUCCESS, handleIntradayLoadSuccess)
}
